import { c as _c } from "react/compiler-runtime";
import { QuickMenu, useQuickMenu } from '@/components/QuickMenu';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { ContentContainer, Layout } from '@/components/layout';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import { Eshop, getEshopDetail, getEshopsSlugs } from '@/sections/discounts/functions/getEshops';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import MoreInfo from '@/sections/MoreInfo';
import SeoPageLayoutSection from '@/components/layout/SeoPageLayoutSections';
import { SeoHeader } from '@/components/SeoHeader';
import { useCoupon } from '@/sections/discounts/hooks/useCoupon';
import { CouponModal } from '@/sections/discounts/CouponModal';
import { getSectionsForEshop } from '@/sections/discounts/functions/getSections';
import React, { useMemo } from 'react';
import Image from 'next/image';
import { SearchResults } from '@/sections/discounts/SearchResults';
import { useEshopDetail } from '@/sections/discounts/hooks/useEshopDetail';
import { Button } from '@/components/buttons';
import { DiscoverRewards } from '@/sections/discounts/DiscoverRewards';
import { Link } from '@/components/links';
import { Headline, Text } from '@/components/typography';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
interface Props extends IntlProps {
  eshop: Eshop;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(97);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["core", "discounts"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    eshop,
    menuPromoBanners
  } = props;
  const {
    activeSection,
    setActiveSection,
    handleScrollToSection,
    getSectionId
  } = useQuickMenu();
  const {
    isVisible,
    coupon,
    setIsVisible
  } = useCoupon();
  let t1;
  if ($[1] !== eshop) {
    t1 = getSectionsForEshop(eshop);
    $[1] = eshop;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const sections = t1;
  const {
    eshopQuery,
    sameCategoryQuery,
    categorySlug
  } = useEshopDetail(eshop.eshopApiId);
  const t2 = eshop.pageSeoTitle || eshop.name || "";
  const t3 = eshop.pageSeoDescription || eshop.name || "";
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = <SeoHeader title={t2} description={t3} />;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== coupon || $[7] !== isVisible || $[8] !== setIsVisible) {
    t5 = <CouponModal isVisible={isVisible} setIsVisible={setIsVisible} coupon={coupon} />;
    $[6] = coupon;
    $[7] = isVisible;
    $[8] = setIsVisible;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== eshop.heroImage || $[11] !== eshop.logo?.asset?.url || $[12] !== eshop.name) {
    t6 = eshop.heroImage?.asset?.url && <div className="bg-blue-50 px-4 pt-28 pb-10 text-center lg:pt-48 lg:pb-14"><div className="bgCircle absolute top-0 left-0 hidden h-[30rem] w-[30rem] -translate-x-1/2 -translate-y-1/2 rotate-180 lg:block lg:h-[50rem] lg:w-[50rem] lg:-translate-x-1/3 lg:-translate-y-1/3" /><div className="relative mx-auto max-w-[1568px]"><div className="coupon-shader absolute top-1/2 left-1/2 block h-full w-full max-w-[1568px] transform rounded-2xl lg:rounded-[40px]" /><Image src={eshop.heroImage.asset.url} alt={eshop.heroImage.alt || ""} width={1568} height={560} className="mx-auto h-56 rounded-2xl lg:h-auto lg:rounded-[40px]" />{eshop.logo?.asset?.url && <Image src={eshop.logo?.asset?.url} alt={eshop.name || ""} width={250} height={200} className="absolute top-1/2 left-1/2 z-10 h-28 w-auto -translate-x-1/2 -translate-y-1/2 transform lg:h-48" />}</div></div>;
    $[10] = eshop.heroImage;
    $[11] = eshop.logo?.asset?.url;
    $[12] = eshop.name;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== eshop.name || $[15] !== t) {
    t7 = t("discounts:eshop.discounts", {
      eshop: eshop.name
    });
    $[14] = eshop.name;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <h2 className="mb-6 text-lg font-semibold lg:mb-12 lg:text-center lg:text-4xl">{t7}</h2>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== eshopQuery) {
    t10 = <div className="mb-20"><SearchResults loadMoreLimit={3} emptyCategories={t9} query={eshopQuery} /></div>;
    $[20] = eshopQuery;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== activeSection || $[23] !== getSectionId || $[24] !== handleScrollToSection || $[25] !== sections) {
    t11 = <QuickMenu activeSection={activeSection} getSectionId={getSectionId} handleScrollToSection={handleScrollToSection} sections={sections} />;
    $[22] = activeSection;
    $[23] = getSectionId;
    $[24] = handleScrollToSection;
    $[25] = sections;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== getSectionId || $[28] !== sections || $[29] !== setActiveSection) {
    let t13;
    if ($[31] !== getSectionId || $[32] !== setActiveSection) {
      t13 = (t14, i) => {
        const {
          content,
          title
        } = t14;
        const sectionId = getSectionId(title.toString());
        return <SeoPageLayoutSection key={`content${i}`} content={content} sectionId={sectionId} i={i} onScroll={setActiveSection} />;
      };
      $[31] = getSectionId;
      $[32] = setActiveSection;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    t12 = sections.map(t13);
    $[27] = getSectionId;
    $[28] = sections;
    $[29] = setActiveSection;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[34] !== t12) {
    t13 = <div className="relative w-full">{t12}</div>;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t11 || $[37] !== t13) {
    t14 = <div className="mt-20 flex flex-col items-start gap-16 lg:flex-row lg:gap-20">{t11}{t13}</div>;
    $[36] = t11;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t10 || $[40] !== t14 || $[41] !== t8) {
    t15 = <div className="bg-blue-50 px-4 pt-10 pb-20 lg:pb-40"><ContentContainer>{t8}{t10}{t14}</ContentContainer></div>;
    $[39] = t10;
    $[40] = t14;
    $[41] = t8;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SectionDelimiter color="bg-blue-100" background="bg-blue-50" />;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t) {
    t17 = t("discounts:eshop.sameCategory");
    $[44] = t;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t17) {
    t18 = <Headline as="h2" variant="h4" className="text-center">{t17}</Headline>;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== categorySlug || $[49] !== sameCategoryQuery || $[50] !== t) {
    t19 = <div className="mt-6 lg:mt-12">{sameCategoryQuery ? <div className="mt-3 lg:mt-5"><SearchResults loadMoreLimit={0} emptyCategories={[]} query={sameCategoryQuery} />{sameCategoryQuery?.data?.pages[0]?.data.length !== 0 && categorySlug && <Button analyticsParam="discountEshopShowAll" testId="discountEshopShowAll" variant="secondary" to={LINKS().discountsCategory(categorySlug)} align="center" size="lg" className="mt-8 lg:mt-12">{t("discounts:eshop.showAll")}</Button>}</div> : <div className="mt-3 lg:mt-5"><Text variant="p2" className="text-center"><Trans><Trans t={t} i18nKey="discounts:eshop.noResults"><Link to={LINKS().discounts} analyticsParam="discountEshopNoCategoryResults" testId="discountEshopNoCategoryResults" /></Trans></Trans></Text><Button analyticsParam="discountEshopShowAllNoResults" testId="discountEshopShowAllNoResults" variant="secondary" to={LINKS().discounts} align="center" size="lg" className="mt-8 lg:mt-12">{t("discounts:eshop.showAll")}</Button></div>}</div>;
    $[48] = categorySlug;
    $[49] = sameCategoryQuery;
    $[50] = t;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t18 || $[53] !== t19) {
    t20 = <div className="bg-blue-100"><div className="px-4 pt-14 pb-20 lg:pt-20 lg:pb-40"><ContentContainer>{t18}{t19}</ContentContainer></div></div>;
    $[52] = t18;
    $[53] = t19;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  let t22;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SectionDelimiter color="bg-blue-50" background="bg-blue-100" />;
    t22 = <DiscoverRewards />;
    $[55] = t21;
    $[56] = t22;
  } else {
    t21 = $[55];
    t22 = $[56];
  }
  let t23;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <IconDeferredPayment className="fillButtonIcon" />;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t) {
    t24 = t("core:products.deferredPayment");
    $[58] = t;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = LINKS();
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t24) {
    t26 = {
      analyticsParam: "discountEshopMoreInfoDeferredPayment",
      icon: t23,
      testId: "discountEshopMoreInfoDeferredPayment",
      title: t24,
      to: t25.deferredPayment
    };
    $[61] = t24;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <IconMaxi className="fillButtonIcon" />;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t) {
    t28 = t("core:products.revolving");
    $[64] = t;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = LINKS();
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t28) {
    t30 = {
      analyticsParam: "discountEshopMoreInfoRevolvingApplication",
      icon: t27,
      testId: "discountEshopMoreInfoRevolvingApplication",
      title: t28,
      to: t29.revolving
    };
    $[67] = t28;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <IconThird className="fillButtonIcon" />;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== t) {
    t32 = t("core:products.payInThree");
    $[70] = t;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = LINKS();
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== t32) {
    t34 = {
      analyticsParam: "discountEshopMoreInfoPayInThree",
      icon: t31,
      testId: "discountEshopMoreInfoPayInThree",
      title: t32,
      to: t33.payInThree
    };
    $[73] = t32;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <IconCard className="fillButtonIcon" />;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== t) {
    t36 = t("core:products.card");
    $[76] = t;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = LINKS();
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] !== t36) {
    t38 = {
      analyticsParam: "discountEshopMoreInfoCard",
      icon: t35,
      testId: "discountEshopMoreInfoCard",
      title: t36,
      to: t37.card
    };
    $[79] = t36;
    $[80] = t38;
  } else {
    t38 = $[80];
  }
  let t39;
  if ($[81] !== t26 || $[82] !== t30 || $[83] !== t34 || $[84] !== t38) {
    t39 = <MoreInfo buttons={[t26, t30, t34, t38]} className="relative z-10" />;
    $[81] = t26;
    $[82] = t30;
    $[83] = t34;
    $[84] = t38;
    $[85] = t39;
  } else {
    t39 = $[85];
  }
  let t40;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[86] = t40;
  } else {
    t40 = $[86];
  }
  let t41;
  if ($[87] !== t39) {
    t41 = <div className="relative overflow-hidden bg-blue-50">{t39}{t40}</div>;
    $[87] = t39;
    $[88] = t41;
  } else {
    t41 = $[88];
  }
  let t42;
  if ($[89] !== menuPromoBanners || $[90] !== t15 || $[91] !== t20 || $[92] !== t4 || $[93] !== t41 || $[94] !== t5 || $[95] !== t6) {
    t42 = <Layout menuPromoBanners={menuPromoBanners}>{t4}{t5}{t6}{t15}{t16}{t20}{t21}{t22}{t41}</Layout>;
    $[89] = menuPromoBanners;
    $[90] = t15;
    $[91] = t20;
    $[92] = t4;
    $[93] = t41;
    $[94] = t5;
    $[95] = t6;
    $[96] = t42;
  } else {
    t42 = $[96];
  }
  return t42;
}
export const getStaticPaths = async () => {
  const eshops = await getEshopsSlugs();
  const paths = eshops.map(eshop => ({
    params: {
      eshop
    }
  }));
  return {
    paths: paths?.filter(Boolean) || [],
    fallback: false
  };
};
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('slevy/[:eshop]');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  const eshop = await getEshopDetail((ctx.params?.eshop as string).toLowerCase());
  return {
    props: {
      eshop,
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      ...(await getIntlProps(ctx, ['discounts']))
    }
  };
}) satisfies GetStaticProps<Props>;